// 3rd-party modules
import { message } from "antd";
import { useEffect, useState } from "react";

// project modules
import Button from "../../shared/button";
import Input from "../../shared/inputs/input";
import Loader from "../../shared/loader";
import { apiCall } from "../../../helpers/apiHelper";

// apis
import * as DashApi from '../../../apis/dashApi';

// models
import { Account } from "../../../models/account";
import { AccountKiboshDevice } from "../../../models/accountKiboshDevice";
import { AccountKiboshDeviceStatus } from "../../../models/accountKiboshDeviceStatus";

type Props = {
  account: Account;
  accountKiboshDevice: AccountKiboshDevice;
  refresh?: boolean;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

const SUPPORTED_BROWSERS_LIST = ["Chrome", "Edge", "Firefox"];

export default function AccountDNSAgents({ account, accountKiboshDevice, refresh, onCancel, onSave }: Props) {
  const [currentKiboshDeviceStatus, setCurrentKiboshDeviceStatus] = useState<AccountKiboshDeviceStatus>(new AccountKiboshDeviceStatus());
  const [isKnownBrowser, setIsKnownBrowser] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  let abortController = new AbortController();

  useEffect(() => {
    let abortController = new AbortController();

    return () => { abortController.abort(); }
  }, []);

  useEffect(() => {
    setIsKnownBrowser(SUPPORTED_BROWSERS_LIST.includes(getCurrentBrowser()));
    getPageAsync();
  }, []);

  useEffect(() => {
    getPageAsync();
  }, [refresh]);

  const getPageAsync = async () => {
    setLoading(true);
    setCurrentKiboshDeviceStatus(await getAccountKiboshDeviceStatus(abortController.signal))
    setLoading(false);
  }

  const getAccountKiboshDeviceStatus = async (abortSignal?: AbortSignal) => {
    const response = await apiCall(DashApi.getKiboshDeviceStatus(accountKiboshDevice?.kiboshDeviceReference!, abortSignal));
    return response.success ? AccountKiboshDeviceStatus.toClass(response.data?.value) : new AccountKiboshDeviceStatus();
  };

  const onDownloadExtentionClick = (browser?: string) => {
    const currentBrowser = browser || getCurrentBrowser();

    switch (currentBrowser) {
      case "Firefox":
        window.open(`https://addons.mozilla.org/en-US/firefox/addon/kiboshdns/`, '_blank', 'noopener,noreferrer');
        break;
      case "Edge":
        window.open(`https://microsoftedge.microsoft.com/addons/detail/kibosh-extension/fdoljggolibgdbpeejbhofjambalhcim`, '_blank', 'noopener,noreferrer');
        break;
      case "Chrome":
        window.open(`https://chromewebstore.google.com/detail/kibosh-extension/hnmiiniomllblemffbiningoljckhnna?authuser=0&hl=en&pli=1`, '_blank', 'noopener,noreferrer');
        break;
      default:
        message.error("At the moment, we only support the Chrome, Brave, Firefox and Edge browsers.");
        break;
    }
  }

  const getCurrentBrowser = () : string => {
    const userAgent = navigator.userAgent;

      if (userAgent.includes("Firefox")) {
        return "Firefox";
      } else if (userAgent.includes("Edg")) {
        return "Edge";
      } else if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
        return "Chrome";
      } else if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
        return "Safari";
      } else if (userAgent.includes("Opera") || userAgent.includes("OPR")) {
        return "Opera";
      } else if (userAgent.includes("Trident")) {
        return "IE";
      } else {
        return "Other";
      }
  }

  const onDocumentsLinkClick = () => {
    // Replace with your desired URL
    window.open('https://kibosh.com/kiboshdns-setup', '_blank', 'noopener,noreferrer');
  }

  return (
    <view data-scroll="">
      {!!loading && <Loader/>}
      <group data-space-top="15" data-space-horizontal="15" data-gap="10" data-direction="column">
        <text data-weight="700" data-text-size="xx-large" data-wrap="wrap" data-color="main-dark">KiboshDNS Browser-Agents</text>
      </group>
      <group data-space="15" data-gap="10">
        <Input
          disabled={true}
          label="DNS ID"
          labelPosition="left"
          dataLength="forcefit"
          size="large"
          value={accountKiboshDevice.kiboshDeviceReference}
        />
        <Input
          label="Password"
          labelPosition="left"
          dataLength="forcefit"
          size="large"
          value={currentKiboshDeviceStatus?.kiboshDevice?.dnsUpdateKey}
          readOnly
        />
      </group>
      <group data-space-horizontal="15" data-gap="10" data-direction="column" data-length="autofit">
        {
          isKnownBrowser ?
            <group data-length="300">
              <Button material primary data-length="autofit" icon="open_in_new" onClick={() => onDownloadExtentionClick("")}>
                <text>Download Extention for this Browser</text>
              </Button>
            </group>
          :
          SUPPORTED_BROWSERS_LIST.map(item =>
            <group data-length="300">
              <Button material primary data-length="autofit" icon="open_in_new" onClick={() => onDownloadExtentionClick(item)}>
                <text>Install the {item} Extention</text>
              </Button>
            </group>
          )
        }
        <text data-weight="700" data-wrap="wrap" data-font-style="italic">
          Supported Browsers: Chrome, Brave, Edge, Firefox
        </text>
        <group data-length="200">
          <Button material outline onClick={onDocumentsLinkClick}>
            <text>Installation Guide</text>
          </Button>
        </group>
      </group>
    </view>
  );
}
